import type { BillingPayer, ChatMessage } from '$lib/api/types.gen';
import { billingPagePath, scopeOfPayer, setUpOffer } from '$lib/utils/billing';
import { billingNotSetUpMessage, BUYING_UNAVAILABLE } from '$lib/utils/billing-messages';

/**
 * A reply the billing gate refused. The refusal arrives as the AI persona's `ChatMessage` with
 * `status: "error"` and an `error_kind`; the payer says whose billing refused it.
 */
export type BillingRefusal =
	/** The payer is out of Dream Bucks. Refusals recorded before the API named the payer have no buy action. */
	| { kind: 'BALANCE_EXHAUSTED'; payer: BillingPayer }
	| { kind: 'BILLING_NOT_PROVISIONED'; payer: BillingPayer | undefined };

/** What the viewer may do about a refusal, and what to call the payer. */
export type BillingRefusalContext = {
	/**
	 * May manage the payer's billing, which is what buying Dream Bucks and setting billing up need
	 * (organization: `org:manage_billing`; user: themselves).
	 */
	canManage: boolean;
	/**
	 * The payer's billing summary says self-serve setup is on. `undefined` when it can't be told: no
	 * summary (the API refused it for want of a billing account, or it couldn't be read) or the
	 * viewer can't see the payer's billing.
	 */
	setupAvailable?: boolean;
	/** The payer's billing is set up now, so a refusal for want of it no longer holds. */
	billingReady?: boolean;
	/**
	 * Purchases are switched on for the payer. `undefined` when it can't be told: the viewer can't
	 * see the payer's billing, or the options couldn't be read.
	 */
	purchasesAvailable?: boolean;
	/**
	 * The payer has Dream Bucks again (a top-up since the refusal), so the refusal no longer holds.
	 * `undefined` when that can't be told: the viewer can't see the payer's balance, or it is unknown.
	 */
	dreamBucksAvailable?: boolean;
	/** The balance snapshot is being refreshed, so `dreamBucksAvailable` may change shortly. */
	balanceRefreshing?: boolean;
	/** May open the payer's Billing page. */
	canViewBilling: boolean;
	organizationName?: string;
};

export type BillingRefusalNotice = {
	text: string;
	/** Offer the buy dialog for the refusal's payer. */
	canBuy: boolean;
	/** Offer to send the refused message again (the payer has Dream Bucks, and there is one to send). */
	canResend?: boolean;
	/** The payer's Billing page, when the viewer may see it. */
	billingPath?: string;
	/** The payer's Billing page, to set billing up there (a payer whose billing isn't set up). */
	setUpPath?: string;
};

/** The refusal behind a message, or `undefined` for any other message (and unknown error kinds). */
export function billingRefusalOf(
	message: Pick<ChatMessage, 'status' | 'error_kind' | 'error_payer'>,
): BillingRefusal | undefined {
	if (message.status !== 'error') return undefined;
	switch (message.error_kind) {
		case 'BALANCE_EXHAUSTED':
			return message.error_payer
				? { kind: 'BALANCE_EXHAUSTED', payer: message.error_payer }
				: undefined;
		case 'BILLING_NOT_PROVISIONED':
			return { kind: 'BILLING_NOT_PROVISIONED', payer: message.error_payer };
		default:
			return undefined;
	}
}

/**
 * Whether the viewer's permissions (and the payer's billing) are worth reading for the notice:
 * `describeBillingRefusal` has nothing to say about an exhausted balance without them, and
 * improves on its plain contact-support text for a payer that isn't set up. Not when there is no
 * payer to read them for.
 */
export function billingRefusalNeedsContext(refusal: BillingRefusal): boolean {
	return refusal.kind === 'BALANCE_EXHAUSTED' || refusal.payer !== undefined;
}

/** What a refusal's notice may offer, and how it words the retry. */
export type BillingRefusalOptions = {
	/** The refused message can be sent again with one click. */
	canResend?: boolean;
	/** What to do once it holds no more, e.g. `try again` for a run that isn't a chat message. */
	retry?: string;
};

const SEND_AGAIN = 'send your message again';

/**
 * What to tell the viewer under a refused reply. An exhausted balance needs the `context` to say
 * anything; a payer without billing is told to contact support until it arrives.
 */
export function describeBillingRefusal(
	refusal: BillingRefusal,
	context?: BillingRefusalContext,
	{ canResend = false, retry = SEND_AGAIN }: BillingRefusalOptions = {},
): BillingRefusalNotice | undefined {
	const Retry = `${retry.charAt(0).toUpperCase()}${retry.slice(1)}`;
	if (refusal.kind === 'BILLING_NOT_PROVISIONED') {
		const subject = refusal.payer?.type === 'org' ? 'organization' : undefined;
		// Nothing known about who may do what yet (or no payer to read it for): ask for support.
		if (!refusal.payer || !context) return { text: billingNotSetUpMessage(subject), canBuy: false };
		// Set up since: whoever read the refusal is better told that than how to set billing up.
		if (context.billingReady) {
			return { text: `Billing is set up now. ${Retry}.`, canBuy: false };
		}
		const offer = setUpOffer({
			setupAvailable: context.setupAvailable,
			canSetUp: context.canManage,
		});
		return {
			text: billingNotSetUpMessage(subject, offer),
			canBuy: false,
			setUpPath: offer === 'set-up' ? billingPagePath(scopeOfPayer(refusal.payer)) : undefined,
		};
	}
	if (!context) return undefined;
	// Topped up since: whoever read the refusal is better told that than how to buy.
	if (context.dreamBucksAvailable) {
		return canResend
			? { text: 'Dream Bucks are available again.', canBuy: false, canResend }
			: { text: `Dream Bucks are available again. ${Retry}.`, canBuy: false };
	}

	const { payer } = refusal;
	// With purchases off nobody can buy, an owner no more than anyone else: only support can add them.
	const purchasesOff = context.purchasesAvailable === false;
	const addDreamBucks = purchasesOff
		? `${BUYING_UNAVAILABLE} Contact support to add Dream Bucks`
		: payer.type === 'org' && !context.canManage
			? `Ask an owner of ${context.organizationName ?? 'the organization'} to add Dream Bucks`
			: 'Add Dream Bucks';
	return {
		text: `${addDreamBucks}, then ${retry}.`,
		canBuy: context.canManage && !purchasesOff,
		billingPath: context.canViewBilling ? billingPagePath(scopeOfPayer(payer)) : undefined,
	};
}

// The buy dialog is URL-driven (`?buy`). Where the page isn't a payer's usage page, the flag
// carries the payer to buy for: `?buy=org:<id>`, or `?buy=user` for the viewer's own billing. A
// user's billing is theirs alone, so the URL never carries a user ID that could be swapped.
// An organization ID is checked as the API's own ID pattern (IDs are short random strings, not
// UUIDs) before it is trusted in an API path.
const BUY_PAYER = /^(?:org:([A-Za-z0-9_-]{1,64})|(user))$/;

/** The payer's identity as a cache key: unlike `buyParamFor`, it keeps the user's ID. */
export function payerKey(payer: BillingPayer): string {
	return `${payer.type}:${payer.id}`;
}

export function buyParamFor(payer: BillingPayer): string {
	return payer.type === 'org' ? `org:${payer.id}` : 'user';
}

/**
 * The payer a `?buy=…` value names, or `undefined` for a bare `?buy`, anything malformed, or
 * `user` without a signed-in user (`userId`) to be the payer.
 */
export function payerFromBuyParam(
	value: string | null,
	userId: string | undefined,
): BillingPayer | undefined {
	const match = value === null ? null : BUY_PAYER.exec(value);
	if (!match) return undefined;
	if (match[1] !== undefined) return { type: 'org', id: match[1] };
	return userId ? { type: 'user', id: userId } : undefined;
}
